import { useEffect, useMemo, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Search as SearchIcon, SlidersHorizontal, Map as MapIcon, List as ListIcon } from "lucide-react";
import BusinessCard from "@/components/BusinessCard";
import MapView from "@/components/MapView";
import {
  Drawer, DrawerTrigger, DrawerContent, DrawerHeader, DrawerTitle, DrawerFooter,
} from "@/components/ui/drawer";

export default function Search() {
  const [sp, setSp] = useSearchParams();
  const [q, setQ] = useState(sp.get("q") || "");
  const [province, setProvince] = useState(sp.get("province") || "all");
  const [district, setDistrict] = useState(sp.get("district") || "all");
  const [categoryId, setCategoryId] = useState(sp.get("category_id") || "all");
  const [sort, setSort] = useState(sp.get("sort") || "newest");
  const [openNow, setOpenNow] = useState(sp.get("open_now") === "1");
  const [view, setView] = useState("list"); // list | map

  const [provinces, setProvinces] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  const userLat = sp.get("user_lat") ? parseFloat(sp.get("user_lat")) : null;
  const userLng = sp.get("user_lng") ? parseFloat(sp.get("user_lng")) : null;

  useEffect(() => {
    api.get("/locations/provinces").then(({ data }) => setProvinces(data));
    api.get("/categories").then(({ data }) => setCategories(data));
  }, []);
  useEffect(() => {
    if (province && province !== "all") api.get("/locations/districts", { params: { province } }).then(({ data }) => setDistricts(data));
    else setDistricts([]);
  }, [province]);

  const load = useCallback(() => {
    setLoading(true);
    const params = { q: q || undefined, sort };
    if (province !== "all") params.province = province;
    if (district !== "all") params.district = district;
    if (categoryId !== "all") params.category_id = categoryId;
    if (openNow) params.open_now = true;
    if (userLat && userLng) { params.user_lat = userLat; params.user_lng = userLng; }
    api.get("/businesses", { params }).then(({ data }) => setResults(data)).finally(() => setLoading(false));
  }, [q, sort, province, district, categoryId, openNow, userLat, userLng]);

  useEffect(() => { load(); }, [load]);

  const submit = (e) => {
    e.preventDefault();
    const p = new URLSearchParams(sp);
    p.set("q", q);
    setSp(p);
  };

  const mapCenter = useMemo(() => {
    if (userLat && userLng) return [userLat, userLng];
    if (results[0]) return [results[0].latitude, results[0].longitude];
    return [39.0, 35.0];
  }, [userLat, userLng, results]);

  return (
    <div className="mx-auto max-w-6xl px-4 py-6">
      <form onSubmit={submit} className="bg-card rounded-2xl border border-border p-3 flex flex-col sm:flex-row gap-2 mb-4">
        <div className="relative flex-1">
          <SearchIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input data-testid="search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Arama..." className="pl-9 h-11 rounded-xl" />
        </div>
        <Drawer>
          <DrawerTrigger asChild>
            <Button data-testid="filters-btn" type="button" variant="outline" className="h-11 rounded-xl">
              <SlidersHorizontal className="w-4 h-4 mr-2" /> Filtreler
            </Button>
          </DrawerTrigger>
          <DrawerContent>
            <DrawerHeader>
              <DrawerTitle>Filtrele</DrawerTitle>
            </DrawerHeader>
            <div className="px-4 pb-4 space-y-3">
              <div>
                <label className="text-sm font-medium">Kategori</label>
                <Select value={categoryId} onValueChange={setCategoryId}>
                  <SelectTrigger data-testid="filter-category" className="mt-1"><SelectValue placeholder="Tümü" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tümü</SelectItem>
                    {categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-sm font-medium">İl</label>
                  <Select value={province} onValueChange={setProvince}>
                    <SelectTrigger data-testid="filter-province" className="mt-1"><SelectValue placeholder="Tümü" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tümü</SelectItem>
                      {provinces.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm font-medium">İlçe</label>
                  <Select value={district} onValueChange={setDistrict} disabled={province === "all"}>
                    <SelectTrigger data-testid="filter-district" className="mt-1"><SelectValue placeholder="Tümü" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tümü</SelectItem>
                      {districts.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <label className="text-sm font-medium">Sıralama</label>
                <Select value={sort} onValueChange={setSort}>
                  <SelectTrigger data-testid="filter-sort" className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="newest">Varsayılan</SelectItem>
                    <SelectItem value="nearest" disabled={!(userLat && userLng)}>En Yakın</SelectItem>
                    <SelectItem value="rating">En Yüksek Puan</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center justify-between pt-2">
                <span className="text-sm font-medium">Şu anda açık olanlar</span>
                <Switch data-testid="filter-open-now" checked={openNow} onCheckedChange={setOpenNow} />
              </div>
            </div>
            <DrawerFooter>
              <Button data-testid="apply-filters" className="rounded-full">Uygula</Button>
            </DrawerFooter>
          </DrawerContent>
        </Drawer>
        <div className="hidden sm:flex gap-1">
          <Button type="button" variant={view === "list" ? "default" : "outline"} onClick={() => setView("list")} className="rounded-xl h-11" data-testid="view-list">
            <ListIcon className="w-4 h-4" />
          </Button>
          <Button type="button" variant={view === "map" ? "default" : "outline"} onClick={() => setView("map")} className="rounded-xl h-11" data-testid="view-map">
            <MapIcon className="w-4 h-4" />
          </Button>
        </div>
      </form>

      <div className="text-sm text-muted-foreground mb-3">
        {loading ? "Yükleniyor..." : `${results.length} sonuç bulundu`}
      </div>

      {view === "map" ? (
        <div className="rounded-2xl overflow-hidden border border-border h-[70vh]">
          <MapView
            center={mapCenter}
            zoom={userLat ? 13 : 6}
            markers={results.filter((r) => r.latitude && r.longitude).map((r) => ({ id: r.id, latitude: r.latitude, longitude: r.longitude, label: r.name }))}
            className="h-full"
          />
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {results.map((b) => (
            <BusinessCard key={b.id} business={b} categories={categories} />
          ))}
        </div>
      )}
    </div>
  );
}
